// Sound utility for button interactions
class SoundManager {
  private audioContext: AudioContext | null = null;
  private isEnabled: boolean = true;

  constructor() {
    // Initialize AudioContext on first user interaction
    this.initializeAudioContext();
  }

  private initializeAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
      this.isEnabled = false;
    }
  }

  private async ensureAudioContext() {
    if (!this.audioContext || !this.isEnabled) return false;

    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (error) {
        console.warn('Failed to resume audio context:', error);
        return false;
      }
    }

    return true;
  }

  // Generate a pop sound using Web Audio API
  async playPopSound(type: 'soft' | 'medium' | 'strong' = 'medium') {
    if (!await this.ensureAudioContext() || !this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      const filterNode = this.audioContext.createBiquadFilter();

      // Connect nodes
      oscillator.connect(filterNode);
      filterNode.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Configure sound based on type
      const config = {
        soft: { freq: 800, gain: 0.1, duration: 0.1 },
        medium: { freq: 1000, gain: 0.15, duration: 0.12 },
        strong: { freq: 1200, gain: 0.2, duration: 0.15 }
      };

      const { freq, gain, duration } = config[type];

      // Set up oscillator
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(freq, this.audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(freq * 0.3, this.audioContext.currentTime + duration);

      // Set up filter for a more pleasant sound
      filterNode.type = 'lowpass';
      filterNode.frequency.setValueAtTime(2000, this.audioContext.currentTime);

      // Set up gain envelope
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(gain, this.audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

      // Play sound
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration);

    } catch (error) {
      console.warn('Failed to play sound:', error);
    }
  }

  // Enable/disable sounds
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
  }

  isAudioEnabled() {
    return this.isEnabled && this.audioContext !== null;
  }
}

// Create singleton instance
export const soundManager = new SoundManager();

// Convenience functions
export const playPopSound = (type?: 'soft' | 'medium' | 'strong') => soundManager.playPopSound(type);
export const enableSounds = (enabled: boolean) => soundManager.setEnabled(enabled);